(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class TeoScrollVideo {
    constructor(root) {
      this.root = root;
      this.scenes = Array.from(root.querySelectorAll('[data-teo-scrub-scene]'));
      this.frameEl = root.querySelector('[data-teo-frame]');
      this.ctx = this.frameEl && this.frameEl.tagName === 'CANVAS' ? this.frameEl.getContext('2d') : null;
      this.frameCount = Number(root.dataset.frameCount) || 0;
      this.frames = [];
      this.frameImages = [];
      this.progress = 0;
      this.currentIndex = -1;
      this.visible = false;
      this.rafId = null;

      this.parseFrames();
      if (!this.frameCount || !this.frames.length || !this.frameEl) return;

      this.preloadFrames();
    }

    parseFrames() {
      const script = this.root.querySelector('script[data-teo-frames-json]');
      try {
        this.frames = script ? JSON.parse(script.textContent) : [];
      } catch (e) {
        this.frames = [];
      }
      this.frameCount = Math.min(this.frameCount, this.frames.length);
    }

    preloadFrames() {
      this.frameImages = [];

      const loadFrame = (index) => {
        if (index >= this.frames.length || this.frameImages[index]) return;
        const img = new Image();
        img.onload = () => {
          if (index === this.currentIndex) this.drawFrame(index);
        };
        img.src = this.frames[index];
        this.frameImages[index] = img;
      };

      // Precarga los primeros 30 frames de inmediato
      for (let i = 0; i < Math.min(30, this.frames.length); i++) {
        loadFrame(i);
      }

      // Precarga el resto en segundo plano
      const preloadRest = () => {
        for (let i = 30; i < this.frames.length; i++) {
          loadFrame(i);
        }
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preloadRest, { timeout: 2000 });
      } else {
        window.setTimeout(preloadRest, 500);
      }

      this.resizeCanvas();
    }

    resizeCanvas() {
      if (!this.frameEl || !this.ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const containerW = this.frameEl.clientWidth || this.frameEl.parentElement.clientWidth || 800;
      const containerH = this.frameEl.clientHeight || this.frameEl.parentElement.clientHeight || 999;
      const scale = Math.min(containerW / 800, containerH / 999);
      const cssW = Math.round(800 * scale);
      const cssH = Math.round(999 * scale);
      const drawW = Math.round(cssW * dpr);
      const drawH = Math.round(cssH * dpr);

      if (this.frameEl.width !== drawW || this.frameEl.height !== drawH) {
        this.frameEl.width = drawW;
        this.frameEl.height = drawH;
        this.frameEl.style.width = `${cssW}px`;
        this.frameEl.style.height = `${cssH}px`;
      }

      if (this.currentIndex >= 0) {
        this.drawFrame(this.currentIndex);
      } else {
        this.drawFrame(0);
      }
    }

    getProgress() {
      const rect = this.root.getBoundingClientRect();
      const scrollableDistance = this.root.offsetHeight - window.innerHeight;
      if (scrollableDistance <= 0) return 0;
      return clamp(-rect.top / scrollableDistance, 0, 1);
    }

    update() {
      this.progress = this.getProgress();
      this.root.style.setProperty('--scrub-progress', this.progress.toFixed(4));
      this.setActiveScene(this.progress);
      this.showFrame(this.progress);
    }

    showFrame(progress) {
      if (!this.frameCount || !this.frameEl) return;
      const index = clamp(Math.floor(progress * this.frameCount), 0, this.frameCount - 1);
      if (index === this.currentIndex) return;
      this.currentIndex = index;
      this.drawFrame(index);
    }

    drawFrame(index) {
      const img = this.frameImages[index];
      if (!img || !img.complete || !img.naturalWidth) return;
      if (!this.ctx) return;

      const canvasW = this.frameEl.width;
      const canvasH = this.frameEl.height;
      this.ctx.clearRect(0, 0, canvasW, canvasH);
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';

      const imgRatio = img.naturalWidth / img.naturalHeight;
      const canvasRatio = canvasW / canvasH;
      let drawW, drawH, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        drawW = canvasW;
        drawH = Math.round(canvasW / imgRatio);
        offsetX = 0;
        offsetY = Math.round((canvasH - drawH) / 2);
      } else {
        drawH = canvasH;
        drawW = Math.round(canvasH * imgRatio);
        offsetY = 0;
        offsetX = Math.round((canvasW - drawW) / 2);
      }

      this.ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
    }

    setActiveScene(progress) {
      if (!this.scenes.length) return;
      const activeIndex = clamp(Math.floor(progress * this.scenes.length), 0, this.scenes.length - 1);
      this.scenes.forEach((scene, index) => {
        scene.classList.toggle('is-active', index === activeIndex);
      });
    }
  }

  class TeoScrollManager {
    constructor() {
      this.instances = [];
      this.rafPending = false;
      this.loopId = null;
      this.observer = null;
      this.isListening = false;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.debounce(() => this.onResize(), 200);
      this.handleTouchMove = this.handleTouchMove.bind(this);
    }

    init() {
      const newInstances = Array.from(document.querySelectorAll('[data-teo-scroll-video]'))
        .map((root) => {
          if (root.dataset.teoScrollReady === 'true') return null;
          root.dataset.teoScrollReady = 'true';
          return new TeoScrollVideo(root);
        })
        .filter(Boolean);

      if (!newInstances.length) return;

      this.instances.push(...newInstances);

      if (!this.isListening) {
        this.isListening = true;
        this.observeVisibility();
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        window.addEventListener('resize', this.handleResize);
      }

      newInstances.forEach((instance) => {
        if (this.observer) this.observer.observe(instance.root);
      });

      this.tick();
      this.scheduleLoop();
    }

    observeVisibility() {
      if (!window.IntersectionObserver) {
        this.instances.forEach((instance) => { instance.visible = true; });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const instance = this.instances.find((i) => i.root === entry.target);
            if (instance) {
              instance.visible = entry.isIntersecting;
            }
          });
          this.scheduleLoop();
        },
        { threshold: 0, rootMargin: '100px' }
      );

      this.instances.forEach((instance) => this.observer.observe(instance.root));
    }

    onResize() {
      this.instances.forEach((instance) => instance.resizeCanvas());
      this.tick();
    }

    handleScroll() {
      if (this.rafPending) return;
      this.rafPending = true;
      window.requestAnimationFrame(() => {
        this.rafPending = false;
        this.tick();
      });
    }

    handleTouchMove() {
      this.handleScroll();
    }

    scheduleLoop() {
      const hasVisible = this.instances.some((i) => i.visible);
      if (hasVisible && !this.loopId) {
        this.startLoop();
      } else if (!hasVisible && this.loopId) {
        this.stopLoop();
      }
    }

    startLoop() {
      if (this.loopId) return;
      const loop = () => {
        this.tick();
        this.loopId = window.requestAnimationFrame(loop);
      };
      this.loopId = window.requestAnimationFrame(loop);
    }

    stopLoop() {
      if (!this.loopId) return;
      window.cancelAnimationFrame(this.loopId);
      this.loopId = null;
    }

    tick() {
      this.instances.forEach((instance) => {
        if (instance.visible) instance.update();
      });
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  const manager = new TeoScrollManager();

  const boot = () => manager.init();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
